import { LOCAL_AUTH_NON_REFRESHABLE_PATHS, LOCAL_AUTH_PATHS } from "./auth-routes";

export type RequestOptions = RequestInit & {
  bodyJson?: unknown;
};

type CreateApiClientConfig<TUser> = {
  baseUrl: string;
  getAccessToken: () => string | null;
  setSession?: (accessToken: string, user: TUser) => void;
  clearSession?: () => void;
  getRefreshPath?: () => string;
  onUnauthorized?: (error: ApiError) => void;
  fetchImpl?: typeof fetch;
};

type RefreshResponse<TUser> = {
  access_token: string;
  refresh_expires_in: number;
  user: TUser;
};

type StructuredErrorEnvelope = {
  code?: unknown;
  message?: unknown;
  trace_id?: unknown;
  params?: unknown;
  details?: unknown;
};

export type ApiErrorContext = {
  requestId?: string | null;
  traceId?: string | null;
};

function normalizeContextValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function formatApiErrorContextSuffix(requestId: string | null, traceId: string | null) {
  const parts = [
    requestId ? `request_id: ${requestId}` : null,
    traceId ? `trace_id: ${traceId}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

function hasApiErrorContext(context: ApiErrorContext) {
  return Boolean(
    normalizeContextValue(context.requestId) || normalizeContextValue(context.traceId),
  );
}

function extractErrorMessage(detail: unknown, status: number) {
  const envelope = extractStructuredErrorEnvelope(detail);
  if (envelope?.message && typeof envelope.message === "string" && envelope.message.trim()) {
    return envelope.message;
  }
  if (detail && typeof detail === "object") {
    const apiMessage = (detail as { message?: unknown }).message;
    if (typeof apiMessage === "string" && apiMessage.trim()) {
      return apiMessage;
    }
  }
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === "object") {
    const nested = (detail as { detail?: unknown }).detail;
    if (typeof nested === "string" && nested.trim()) {
      return nested;
    }
    if (Array.isArray(nested)) {
      const messages = nested
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }
          if (item && typeof item === "object" && "msg" in item && typeof item.msg === "string") {
            return item.msg;
          }
          return null;
        })
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join("; ");
      }
    }
  }
  return `Request failed with status ${status}`;
}

function extractStructuredErrorEnvelope(detail: unknown) {
  if (!detail || typeof detail !== "object") {
    return null;
  }
  const record = detail as StructuredErrorEnvelope & {
    detail?: unknown;
  };
  const hasStructuredFields = (candidate: StructuredErrorEnvelope) =>
    (typeof candidate.code === "string" && candidate.code.trim()) ||
    (typeof candidate.message === "string" && candidate.message.trim()) ||
    (typeof candidate.trace_id === "string" && candidate.trace_id.trim());
  const normalizeEnvelope = (candidate: StructuredErrorEnvelope): StructuredErrorEnvelope => ({
    code: candidate.code,
    message: candidate.message,
    trace_id: candidate.trace_id,
    params: candidate.params ?? candidate.details,
  });
  if (hasStructuredFields(record)) {
    return normalizeEnvelope(record);
  }
  if (record.detail && typeof record.detail === "object") {
    const nested = record.detail as StructuredErrorEnvelope;
    if (hasStructuredFields(nested)) {
      return normalizeEnvelope(nested);
    }
  }
  return null;
}

function extractStructuredErrorParams(detail: unknown) {
  const envelope = extractStructuredErrorEnvelope(detail);
  if (!envelope) {
    return null;
  }
  if (envelope.params && typeof envelope.params === "object" && !Array.isArray(envelope.params)) {
    return envelope.params as Record<string, unknown>;
  }
  return null;
}

async function readErrorDetail(response: Response) {
  const fallback = response.statusText;
  const text = await response.text();
  if (!text) {
    return fallback;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  return text;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  code: string | null;
  requestId: string | null;
  traceId: string | null;
  params: Record<string, unknown> | null;
  details: Record<string, unknown> | null;
  diagnosticMessage: string | null;
  private readonly baseMessage: string;

  constructor(status: number, detail: unknown, context: ApiErrorContext = {}) {
    const baseMessage = extractErrorMessage(detail, status);
    super(baseMessage);
    this.baseMessage = baseMessage;
    this.status = status;
    this.detail = detail;
    this.code = null;
    this.requestId = normalizeContextValue(context.requestId);
    this.traceId = normalizeContextValue(context.traceId);
    this.params = null;
    this.details = null;
    this.diagnosticMessage = null;
    const record = extractStructuredErrorEnvelope(detail);
    if (record) {
      this.code = typeof record.code === "string" && record.code.trim() ? record.code.trim() : null;
      this.traceId = normalizeContextValue(record.trace_id) ?? this.traceId;
      this.params = extractStructuredErrorParams(detail);
      this.details = this.params;
      this.diagnosticMessage =
        typeof record.message === "string" && record.message.trim() ? record.message.trim() : null;
    }
    this.syncMessageContext();
  }

  private syncMessageContext() {
    this.message = `${this.baseMessage}${formatApiErrorContextSuffix(this.requestId, this.traceId)}`;
  }

  withContext(context: ApiErrorContext) {
    this.requestId = this.requestId ?? normalizeContextValue(context.requestId);
    this.traceId = this.traceId ?? normalizeContextValue(context.traceId);
    this.syncMessageContext();
    return this;
  }
}

export async function readApiErrorDetail(response: Response) {
  return readErrorDetail(response);
}

export function readApiErrorContext(response: Pick<Response, "headers">): ApiErrorContext {
  return {
    requestId: response.headers.get("x-request-id"),
    traceId: response.headers.get("x-trace-id"),
  };
}

export function createApiErrorFromResponse(response: Response, detail: unknown) {
  return new ApiError(response.status, detail, readApiErrorContext(response));
}

export function attachApiErrorContext(error: unknown, context: ApiErrorContext, status = 500) {
  if (error instanceof ApiError) {
    return error.withContext(context);
  }
  if (error instanceof Error && hasApiErrorContext(context)) {
    return new ApiError(status, { message: error.message }, context);
  }
  return error;
}

export function createApiClient<TUser>(config: CreateApiClientConfig<TUser>) {
  const fetchImpl = config.fetchImpl ?? fetch;
  let refreshPromise: Promise<string | null> | null = null;

  function handleUnauthorized(detail: unknown, context: ApiErrorContext = {}) {
    const error = new ApiError(401, detail, context);
    config.clearSession?.();
    config.onUnauthorized?.(error);
    return error;
  }

  async function parseResponseBody<T>(response: Response): Promise<T> {
    const createEmptyJsonError = () =>
      createApiErrorFromResponse(response, {
        code: "platform.api.json_response_empty",
        message:
          response.status === 204
            ? "Expected JSON response body but received 204 No Content."
            : "Expected JSON response body but received an empty response body.",
      });

    if (response.status === 204) {
      throw createEmptyJsonError();
    }

    const contentLength = response.headers.get("content-length");
    if (contentLength === "0") {
      throw createEmptyJsonError();
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const text = await response.text();
      if (text.length === 0) {
        throw createEmptyJsonError();
      }
      return text as T;
    }

    const text = await response.text();
    if (text.length === 0) {
      throw createEmptyJsonError();
    }

    return JSON.parse(text) as T;
  }

  async function refreshAccessToken(): Promise<string | null> {
    const setSession = config.setSession;
    if (!setSession) {
      return null;
    }
    if (!refreshPromise) {
      refreshPromise = (async () => {
        const refreshPath = config.getRefreshPath?.() ?? LOCAL_AUTH_PATHS.refresh;
        const response = await fetchImpl(`${config.baseUrl}${refreshPath}`, {
          method: "POST",
          credentials: "include",
        });
        if (!response.ok) {
          const detail = await readErrorDetail(response);
          throw createApiErrorFromResponse(response, detail);
        }
        const data = await parseResponseBody<RefreshResponse<TUser>>(response);
        setSession(data.access_token, data.user);
        return data.access_token;
      })().finally(() => {
        refreshPromise = null;
      });
    }
    try {
      return await refreshPromise;
    } catch {
      return null;
    }
  }

  async function sendRequest(
    path: string,
    options: RequestOptions = {},
    accessTokenOverride?: string | null,
  ): Promise<Response> {
    const { bodyJson, headers, ...rest } = options;
    const token = accessTokenOverride ?? config.getAccessToken();
    const requestBody = bodyJson === undefined ? rest.body : JSON.stringify(bodyJson);
    const hasMultipartBody = typeof FormData !== "undefined" && requestBody instanceof FormData;
    const normalizedHeaders: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers as Record<string, string> | undefined),
    };
    if (
      !hasMultipartBody &&
      !Object.keys(normalizedHeaders).some((key) => key.toLowerCase() === "content-type")
    ) {
      normalizedHeaders["Content-Type"] = "application/json";
    }
    const requestInit: RequestInit = {
      ...rest,
      credentials: "include",
      headers: normalizedHeaders,
    };
    if (requestBody !== undefined) {
      requestInit.body = requestBody;
    }
    return fetchImpl(`${config.baseUrl}${path}`, requestInit);
  }

  async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response = await sendRequest(path, options);

    if (!response.ok) {
      const detail = await readErrorDetail(response);
      const context = readApiErrorContext(response);
      if (response.status === 401) {
        const canRefresh = !LOCAL_AUTH_NON_REFRESHABLE_PATHS.has(path);
        if (canRefresh) {
          const refreshedAccessToken = await refreshAccessToken();
          if (refreshedAccessToken) {
            response = await sendRequest(path, options, refreshedAccessToken);
            if (response.ok) {
              return parseResponseBody<T>(response);
            }
          }
        }
        throw handleUnauthorized(detail, context);
      }
      throw createApiErrorFromResponse(response, detail);
    }

    return parseResponseBody<T>(response);
  }

  async function downloadRequest(path: string, filename?: string): Promise<void> {
    let response = await sendRequest(path, { method: "GET" });

    if (!response.ok) {
      const detail = await readErrorDetail(response);
      const context = readApiErrorContext(response);
      if (response.status === 401) {
        const refreshedAccessToken = await refreshAccessToken();
        if (refreshedAccessToken) {
          response = await sendRequest(path, { method: "GET" }, refreshedAccessToken);
        }
        if (!response.ok) {
          throw handleUnauthorized(detail, context);
        }
      } else {
        throw createApiErrorFromResponse(response, detail);
      }
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = filename ?? "download";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);
  }

  return {
    apiRequest,
    downloadRequest,
  };
}
